import { compileShader } from './shader';

export class Program {
  constructor(
    readonly program: WebGLProgram,
    readonly uniforms: ReadonlyMap<string, WebGLUniformLocation | null>,
    readonly attributes: ReadonlyMap<string, number>,
  ) {}

  u(name: string): WebGLUniformLocation | null {
    return this.uniforms.get(name) ?? null;
  }

  a(name: string): number {
    return this.attributes.get(name) ?? -1;
  }
}

export interface ProgramKey {
  id: string;
  defines: number;
}

const keyHash = (k: ProgramKey): string => `${k.id}#${k.defines.toString(16)}`;

const injectDefines = (source: string, defineBlock: string): string => {
  if (defineBlock === '') return source;
  const newline = source.indexOf('\n');
  // GLSL ES requires #version on the very first line
  return source.slice(0, newline + 1) + defineBlock + source.slice(newline + 1);
};

export class ProgramCache {
  private readonly cache = new Map<string, Program>();
  private readonly defineNames = new Map<number, string>();
  constructor(private readonly gl: WebGL2RenderingContext) {}

  registerDefine(bit: number, name: string): void {
    this.defineNames.set(bit, name);
  }

  private buildDefineBlock(defines: number): string {
    if (defines === 0) return '';
    let out = '';
    for (const [bit, name] of this.defineNames) {
      if ((defines & bit) !== 0) out += `#define ${name}\n`;
    }
    return out;
  }

  acquire(
    key: ProgramKey,
    vsSource: string,
    fsSource: string,
    uniformNames: readonly string[],
    attributeNames: readonly string[],
    uniformBlockBindings?: ReadonlyArray<{ name: string; binding: number }>,
  ): Program {
    const hash = keyHash(key);
    const cached = this.cache.get(hash);
    if (cached !== undefined) return cached;

    const gl = this.gl;
    const defines = this.buildDefineBlock(key.defines);
    const vs = compileShader(gl, gl.VERTEX_SHADER, injectDefines(vsSource, defines));
    const fs = compileShader(gl, gl.FRAGMENT_SHADER, injectDefines(fsSource, defines));

    const program = gl.createProgram();
    if (program === null) throw new Error('gl.createProgram returned null');
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (gl.getProgramParameter(program, gl.LINK_STATUS) !== true) {
      const log = gl.getProgramInfoLog(program) ?? '<no info log>';
      gl.deleteProgram(program);
      throw new Error(`program ${key.id}#${key.defines} link failed: ${log}`);
    }

    const uniforms = new Map<string, WebGLUniformLocation | null>();
    for (const name of uniformNames) uniforms.set(name, gl.getUniformLocation(program, name));
    const attributes = new Map<string, number>();
    for (const name of attributeNames) attributes.set(name, gl.getAttribLocation(program, name));

    if (uniformBlockBindings !== undefined) {
      for (const b of uniformBlockBindings) {
        const idx = gl.getUniformBlockIndex(program, b.name);
        if (idx !== gl.INVALID_INDEX) gl.uniformBlockBinding(program, idx, b.binding);
      }
    }

    const entry = new Program(program, uniforms, attributes);
    this.cache.set(hash, entry);
    return entry;
  }

  size(): number {
    return this.cache.size;
  }

  dispose(): void {
    for (const p of this.cache.values()) this.gl.deleteProgram(p.program);
    this.cache.clear();
  }
}
